import { Cross2Icon, TargetIcon } from '@radix-ui/react-icons';
import { Box, Button, Card, Flex, Text } from '@radix-ui/themes';
import { CommonConfirmBallType } from 'components/common/dialogs/confirm-ball-type';
import { ErrorBoundary } from 'components/common/error-boundary';
import { useHardwareDiagnosticStore } from 'components/machine/dialogs/hardware-diagnostic/store/use-hardware-diagnostic-store';
import { CommonNavMenu } from 'components/common/layout/nav-menu';
import { SidebarNavigationTrigger } from 'components/main/sidebar/nav-trigger';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { t } from 'i18next';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import { useCallback, useContext, useMemo, useState } from 'react';

interface IProps {
  expanded: boolean;
  hwd: IDataCollection;
}

export const HardwareDiagnosticReminder = (props: IProps) => {
  const { machine, openDialog } = useContext(MachineContext);
  const tryCancel = useHardwareDiagnosticStore((s) => s.tryCancel);

  const icon = <TargetIcon />;

  // remind user to change balls in hopper
  const [dialogBall, setDialogBall] = useState<number>();

  const handleClick = useCallback(() => {
    if (props.hwd.ball_type !== machine.ball_type) {
      // show alert re: changing ball type first
      setDialogBall(Date.now());
      return;
    }

    openDialog(MachineDialogMode.HwDiagnostic, {
      hwd: props.hwd,
    });
  }, [props.hwd.ball_type, machine.ball_type]);

  const content = useMemo(() => {
    const tooltip = `${t('common.hardware-diagnostic')}: ${
      props.hwd.ball_type
    }`;

    if (!props.expanded) {
      return (
        <CommonNavMenu title={tooltip}>
          <SidebarNavigationTrigger
            label={tooltip}
            icon={icon}
            onClick={handleClick}
            active
          />
        </CommonNavMenu>
      );
    }

    return (
      <Card title={tooltip}>
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <Box className="valign-center" pt="1">
              {icon}
            </Box>

            <Box
              flexGrow="1"
              overflow="hidden"
              style={{ textOverflow: 'ellipsis' }}
            >
              <Text truncate>{t('common.hardware-diagnostic')}</Text>
            </Box>
          </Flex>

          <Flex gap="2">
            <Button color="gray" variant="soft" onClick={() => tryCancel()}>
              <Box className="valign-center" pt="1">
                <Cross2Icon />
              </Box>
            </Button>
            <Button style={{ flex: 1 }} onClick={handleClick}>
              {t('common.resume')}
            </Button>
          </Flex>
        </Flex>
      </Card>
    );
  }, [props.expanded, props.hwd.ball_type, handleClick, tryCancel]);

  return (
    <ErrorBoundary identifier="HardwareDiagnosticReminder">
      {content}

      {dialogBall && (
        <CommonConfirmBallType
          key={dialogBall}
          nextBallType={props.hwd.ball_type}
          onContinue={() => {
            setDialogBall(undefined);
            openDialog(MachineDialogMode.HwDiagnostic, {
              hwd: props.hwd,
            });
          }}
        />
      )}
    </ErrorBoundary>
  );
};
